/**
 * Tuning constants + pre-allocated lookup tables for the player robot.
 *
 * Pulled out of `Robot.ts` so the class file shrinks toward orchestration-only
 * and so later extractions (RobotVision, RobotPathing, RobotWeapons) can
 * import the same values without circular deps. Values are identical to the
 * originals — this is mechanical relocation, not a balance pass.
 */

import type { WeaponCategory } from '../upgrades/WeaponData';

// =======================================================
// Physical / collision
// =======================================================

/** Half-width of the robot's collision box, in tile units. */
export const ROBOT_RADIUS = 0.3;

/** Clearance radius used when planning / smoothing paths — wider than
 *  `ROBOT_RADIUS` so the spline doesn't hug walls at full speed. */
export const PATH_CLEARANCE = 0.5;

// =======================================================
// Combat
// =======================================================

/** Pickup range for loot chests (tile units). */
export const LOOT_RANGE = 0.6;

/** Unarmed melee damage — applied when no weapons are equipped. */
export const BASE_ROBOT_DAMAGE = 3;

// =======================================================
// Turret
// =======================================================

/** Baseline turret turn rate (rad/s), multiplied by `stats.turretSpeed`. */
export const BASE_TURRET_TURN_SPEED = Math.PI * 3;

/** Idle turret sweep oscillation speed (rad/s). */
export const TURRET_SWEEP_SPEED = 0.8;

/** Max angular error at which a turret-aimed weapon is allowed to fire. */
export const TURRET_AIM_THRESHOLD = 0.15;

/** Side-hardpoint turn rate (rad/s) — deliberately slower than the main
 *  turret so flankers visibly lag when snapping to a secondary target. */
export const SIDE_HARDPOINT_TURN_SPEED = Math.PI * 2.5;

// =======================================================
// Evasive maneuver tuning
// =======================================================

/** Strafe speed as a fraction of `combatApproachSpeed`. */
export const EVASION_STRAFE_SPEED_FRAC = 0.55;

/** Minimum seconds before the robot randomly flips strafe direction. */
export const EVASION_DIR_CHANGE_MIN = 1.2;

/** Maximum seconds before the robot randomly flips strafe direction. */
export const EVASION_DIR_CHANGE_MAX = 3.0;

// =======================================================
// Pre-allocated direction tables
// =======================================================
//
// These exist so hot-path loops (`revealVision`, `pickExplorationTarget`) can
// iterate neighbours without allocating a `[dx, dy][]` array per tile per
// frame. `as const` readonly so a caller accidentally pushing into them is a
// compile error.

export const CARDINAL_DIRS: readonly [number, number][] = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
];

export const ALL_DIRS: readonly [number, number][] = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [1, 1], [1, -1], [-1, 1], [-1, -1],
];

// =======================================================
// Weapon slot lateral offsets
// =======================================================

/**
 * Per-weapon lateral offset (perpendicular to turret), in tile units,
 * indexed by `slotCount - 1` then by slot index. Matches the visual
 * `SLOT_OFFSETS` in `RobotGraphic` so muzzle flashes emerge from the
 * correct barrel.
 */
export const WEAPON_LATERAL_OFFSETS: readonly (readonly number[])[] = [
  [0],                           // 1 slot: center
  [-0.10, 0.10],                 // 2 slots: symmetric pair
  [0, -0.14, 0.14],              // 3 slots: center + flankers
  [-0.07, 0.07, -0.15, 0.15],    // 4 slots: inner + outer pairs
];

// =======================================================
// Tier-indexed muzzle-flash colors
// =======================================================
//
// Indexed by `tier - 1` (tiers 1..5). Allocated once at module load instead
// of rebuilding the palette for every shot. Caller falls back to the tier-1
// color if the tier is out of range (shouldn't happen, but cheap insurance).

export const BALLISTIC_FLASH_COLORS: readonly number[] = [
  0xffcc44, 0xffdd55, 0xffee66, 0xffffff, 0xaaeeff,
];

export const ENERGY_COLORS: readonly number[] = [
  0x44ccff, 0x66ddff, 0x88eeff, 0xaaffff, 0xffffff,
];

export const ROCKET_FLASH_COLORS: readonly number[] = [
  0xff8833, 0xff9944, 0xffaa55, 0xffbb66, 0xffdd88,
];

export const BOMB_FLASH_COLORS: readonly number[] = [
  0xff4400, 0xff5500, 0xff6611, 0xff8822, 0xffaa44,
];

export const MELEE_COLORS: readonly number[] = [
  0xffaa00, 0xffbb22, 0xffcc44, 0xffdd66, 0xffffff,
];

/** Per-category SFX path. Keyed map avoids a switch in every fire path. */
export const WEAPON_SFX: Readonly<Record<WeaponCategory, string>> = {
  ballistic: '/sound/ballistic-weapon.mp3',
  energy:    '/sound/energy-weapon.mp3',
  rocket:    '/sound/missile-weapon.mp3',
  bomb:      '/sound/bomb-weapon.mp3',
  melee:     '/sound/melee-weapon.mp3',
};
